import { ICoursesInPerson } from '../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { IFormData } from '../../pages/Dashboard/Courses/Courses/CoursesModal'
import apiService from '../Api'
import { appendCoursePageContent } from './appendCoursePageContent'
import { IBulkPriceSchedule, IBulkPriceSchedulePaginated, ICourse, ISelectCourse, TBulkModality } from './interfaces'

export class CoursesService {
  async list(
    search: string,
    page: number,
    per_page: number
  ): Promise<{ total_filtered: number; data: ICoursesInPerson[] }> {
    return apiService
      .get('/courses', {
        params: {
          search,
          page,
          per_page
        }
      })
      .then((res) => res.data)
  }

  async getCourse(id: number): Promise<ICourse> {
    return apiService.get(`/courses/${id}`).then((res) => res.data)
  }

  async getKnowledgeAreas() {
    return apiService.get(`/courses/knowledge-areas`).then((res) => res.data)
  }

  async getDynamics() {
    return apiService.get(`/courses/dynamics`).then((res) => res.data)
  }

  async getObjectives() {
    return apiService.get(`/courses/objectives`).then((res) => res.data)
  }

  async getActiveCampaignLists() {
    return apiService.get(`/courses/active-campaign-lists`).then((res) => res.data.data)
  }

  async create(data: IFormData) {
    const formData = new FormData()

    formData.append('name', data.name)
    if (data.thumbnail) {
      formData.append('thumbnail', data.thumbnail)
    }
    formData.append('course_link', data.course_link)
    formData.append('taxa_matricula_in_person', String(data.taxa_matricula_in_person ?? 0))
    formData.append('full_price_in_person', String(data.full_price_in_person ?? 0))
    formData.append(
      'discount_percentage_in_person',
      data.discount_percentage_in_person.replace('.', '').replace(',', '.')
    )
    formData.append('installments_in_person', data.installments_in_person)
    formData.append('taxa_matricula_live_online', String(data.taxa_matricula_live_online ?? 0))
    formData.append('full_price_live_online', String(data.full_price_live_online ?? 0))
    formData.append(
      'discount_percentage_live_online',
      data.discount_percentage_live_online.replace('.', '').replace(',', '.')
    )
    formData.append('duration_in_hours', String(data.duration_in_hours))
    formData.append('promotional_bar', data.promotional_bar)
    formData.append('duration_in_months', String(data.duration_in_months))
    formData.append('in_person', data.in_person ? '1' : '0')
    formData.append('live_online', data.live_online ? '1' : '0')
    formData.append('is_technical', data.is_technical ? '1' : '0')

    if (data.cta_text_in_person) {
      formData.append('cta_text_in_person', data.cta_text_in_person)
    }
    if (data.cta_text_live_online) {
      formData.append('cta_text_live_online', data.cta_text_live_online)
    }

    if (data.promotional_bar === '1') {
      formData.append('cronometer_type', data.cronometer_type!)

      if (data.cronometer_type === 'end_date') {
        formData.append('cronometer_end_date', data.cronometer_end_date!)
      }

      if (data.cronometer_type === 'time') {
        formData.append('cronometer_time', data.cronometer_time!)
      }
    }

    data.knowledge_area_ids.forEach((area) => {
      formData.append('knowledge_area_ids[]', area)
    })
    data.objective_ids.forEach((objective) => {
      formData.append('objective_ids[]', objective)
    })
    data.dynamic_ids.forEach((dynamic) => {
      formData.append('dynamic_ids[]', dynamic)
    })
    data.active_campaign_in_person_lists.forEach((inPersonCampaign) => {
      formData.append('active_campaign_in_person_lists[]', inPersonCampaign)
    })
    data.active_campaign_live_online_lists.forEach((onlineCampaign) => {
      formData.append('active_campaign_live_online_lists[]', onlineCampaign)
    })

    appendCoursePageContent(formData, data)

    return apiService.post('/courses', formData).then((res) => res.data)
  }

  async update(id: number, data: IFormData) {
    const formData = new FormData()

    formData.append('_method', 'PUT')
    formData.append('name', data.name)
    if (data.thumbnail && data.thumbnail instanceof File) {
      formData.append('thumbnail', data.thumbnail)
    }
    formData.append('course_link', data.course_link)
    formData.append('taxa_matricula_in_person', String(data.taxa_matricula_in_person ?? 0))
    formData.append('full_price_in_person', String(data.full_price_in_person ?? 0))
    formData.append(
      'discount_percentage_in_person',
      data.discount_percentage_in_person.replace('.', '').replace(',', '.')
    )
    formData.append('installments_in_person', data.installments_in_person)
    formData.append('taxa_matricula_live_online', String(data.taxa_matricula_live_online ?? 0))
    formData.append('full_price_live_online', String(data.full_price_live_online ?? 0))
    formData.append(
      'discount_percentage_live_online',
      data.discount_percentage_live_online.replace('.', '').replace(',', '.')
    )
    formData.append('duration_in_hours', String(data.duration_in_hours))
    formData.append('promotional_bar', data.promotional_bar)
    formData.append('duration_in_months', String(data.duration_in_months))
    formData.append('in_person', data.in_person ? '1' : '0')
    formData.append('live_online', data.live_online ? '1' : '0')
    formData.append('is_technical', data.is_technical ? '1' : '0')

    if (data.cta_text_in_person !== undefined) {
      formData.append('cta_text_in_person', data.cta_text_in_person)
    }
    if (data.cta_text_live_online !== undefined) {
      formData.append('cta_text_live_online', data.cta_text_live_online)
    }

    if (data.promotional_bar === '1') {
      formData.append('cronometer_type', data.cronometer_type!)

      if (data.cronometer_type === 'end_date') {
        formData.append('cronometer_end_date', data.cronometer_end_date!)
      }

      if (data.cronometer_type === 'time') {
        formData.append('cronometer_time', data.cronometer_time!)
      }
    }

    data.knowledge_area_ids.forEach((area) => {
      formData.append('knowledge_area_ids[]', area)
    })
    data.objective_ids.forEach((objective) => {
      formData.append('objective_ids[]', objective)
    })
    data.dynamic_ids.forEach((dynamic) => {
      formData.append('dynamic_ids[]', dynamic)
    })
    data.active_campaign_in_person_lists.forEach((inPersonCampaign) => {
      formData.append('active_campaign_in_person_lists[]', inPersonCampaign)
    })
    data.active_campaign_live_online_lists.forEach((onlineCampaign) => {
      formData.append('active_campaign_live_online_lists[]', onlineCampaign)
    })

    appendCoursePageContent(formData, data)

    return apiService.post(`/courses/${id}`, formData).then((res) => res.data)
  }

  async updateEnable(id: number, enabled: boolean) {
    return apiService.put(`/courses/${id}`, {
      enabled: enabled ? '1' : '0'
    })
  }

  async updateFeatured(id: number, isFeatured: boolean) {
    return apiService.put(`/courses/${id}`, {
      is_featured: isFeatured ? '1' : '0'
    })
  }

  async updateShowOnHomepage(id: number, showOnHomepage: boolean) {
    return apiService.put(`/courses/${id}`, {
      show_on_homepage: showOnHomepage ? '1' : '0'
    })
  }

  async delete(id: number) {
    return apiService.delete(`/courses/${id}`)
  }

  async listForSelect(in_person?: boolean, live_online?: boolean, recorded?: boolean): Promise<ISelectCourse[]> {
    if (in_person === undefined && live_online === undefined && recorded === undefined) {
      return apiService.get('/courses/select').then((response) => response.data)
    }

    const params: Record<string, string> = {}
    if (in_person !== undefined) params.in_person = in_person ? '1' : '0'
    if (live_online !== undefined) params.live_online = live_online ? '1' : '0'
    if (recorded !== undefined) params.recorded = recorded ? '1' : '0'

    return apiService.get('/courses/select', { params }).then((response) => response.data)
  }

  /** Inclui os desativados: o relatório de NPS filtra respostas de cursos que saíram do catálogo. */
  async listForSelectIncludingDisabled(): Promise<ISelectCourse[]> {
    return apiService.get('/courses/select', { params: { include_disabled: '1' } }).then((response) => response.data)
  }

  async bulkUpdatePrices(payload: {
    entries: { course_id: number; price_type: 'in_person' | 'live_online' | 'recorded' }[]
    mode: 'discount' | 'prices'
    discount?: number
    taxa_matricula?: number
    price_original?: number
    cta_text?: string
  }): Promise<{ message: string; updated: number; skipped: number }> {
    return apiService.put('/courses/bulk-prices', payload).then((res) => res.data)
  }

  async listBulkPriceSchedules(params: {
    modality?: TBulkModality
    page?: number
    per_page?: number
  }): Promise<IBulkPriceSchedulePaginated> {
    return apiService.get('/courses/bulk-price-schedules', { params }).then((res) => res.data)
  }

  async createBulkPriceSchedule(payload: Record<string, unknown>): Promise<IBulkPriceSchedule> {
    return apiService.post('/courses/bulk-price-schedules', payload).then((res) => res.data)
  }

  async updateBulkPriceSchedule(id: number, payload: Record<string, unknown>): Promise<IBulkPriceSchedule> {
    return apiService.put(`/courses/bulk-price-schedules/${id}`, payload).then((res) => res.data)
  }

  async deleteBulkPriceSchedule(id: number): Promise<void> {
    return apiService.delete(`/courses/bulk-price-schedules/${id}`).then((res) => res.data)
  }
}
